import React, { useState, useRef, useEffect, useCallback } from "react";
import "../styles/Car360Viewer.css";
import InteriorVideoPlayer from "./car/InteriorVideoPlayer";
import CarHotspotViewer from "./car/CarHotspotViewer";

const Car360Viewer = ({
  images = [],
  videoUrl = null,
  type = "exterior",
  car = null,
  onImageChange,
}) => {
  const [currentIndex, setCurrentIndex]     = useState(0);
  const [isDragging, setIsDragging]         = useState(false);
  const [isAutoRotating, setIsAutoRotating] = useState(false);
  const [dragHint, setDragHint]             = useState(true);

  const containerRef    = useRef(null);
  const autoRotateRef   = useRef(null);
  const isDraggingRef   = useRef(false);
  const startXRef       = useRef(0);
  const lastXRef        = useRef(0);
  const accumulatedRef  = useRef(0);   // sub-frame accumulator
  const currentIdxRef   = useRef(0);   // mirror of state for handlers
  const velocityRef     = useRef(0);   // for momentum
  const momentumRef     = useRef(null);
  const lastTimeRef     = useRef(0);
  const hintTimerRef    = useRef(null);

  // Keep ref in sync with state
  useEffect(() => { currentIdxRef.current = currentIndex; }, [currentIndex]);
  useEffect(() => { setCurrentIndex(0); accumulatedRef.current = 0; }, [images, type]);

  // Hide drag hint after 3 s
  useEffect(() => {
    hintTimerRef.current = setTimeout(() => setDragHint(false), 3000);
    return () => clearTimeout(hintTimerRef.current);
  }, []);

  // Cleanup on unmount
  useEffect(() => () => {
    cancelMomentum();
    if (autoRotateRef.current) clearInterval(autoRotateRef.current);
  }, []);

  // ── helpers ────────────────────────────────────────────────────────────────

  const totalFrames = images?.length || 1;

  /**
   * SENSITIVITY: pixels per full 360° rotation.
   * Lower = faster spin (Spinny feels ~400–600 px per revolution).
   */
  const PX_PER_REVOLUTION = 420;

  const advanceByDelta = useCallback((deltaX) => {
    if (!images?.length) return;
    accumulatedRef.current += deltaX;
    const pxPerFrame = PX_PER_REVOLUTION / totalFrames;
    const steps = Math.round(accumulatedRef.current / pxPerFrame);
    if (steps === 0) return;
    accumulatedRef.current -= steps * pxPerFrame;
    const ni = ((currentIdxRef.current - steps) % totalFrames + totalFrames) % totalFrames;
    currentIdxRef.current = ni;
    setCurrentIndex(ni);
    if (onImageChange) onImageChange(ni);
  }, [images, totalFrames, onImageChange]);

  const cancelMomentum = () => {
    if (momentumRef.current) { cancelAnimationFrame(momentumRef.current); momentumRef.current = null; }
  };

  const applyMomentum = useCallback(() => {
    if (Math.abs(velocityRef.current) < 0.3) { velocityRef.current = 0; return; }
    advanceByDelta(velocityRef.current);
    velocityRef.current *= 0.88;   // friction — tune 0.85–0.92
    momentumRef.current = requestAnimationFrame(applyMomentum);
  }, [advanceByDelta]);

  // ── Mouse handlers ─────────────────────────────────────────────────────────

  const onMouseDown = useCallback((e) => {
    e.preventDefault();
    cancelMomentum();
    if (autoRotateRef.current) { clearInterval(autoRotateRef.current); setIsAutoRotating(false); }
    isDraggingRef.current = true;
    setIsDragging(true);
    setDragHint(false);
    startXRef.current    = e.clientX;
    lastXRef.current     = e.clientX;
    velocityRef.current  = 0;
    lastTimeRef.current  = performance.now();
    accumulatedRef.current = 0;
  }, []);

  const onMouseMove = useCallback((e) => {
    if (!isDraggingRef.current) return;
    const now   = performance.now();
    const dt    = now - lastTimeRef.current;
    const dx    = e.clientX - lastXRef.current;
    lastXRef.current    = e.clientX;
    lastTimeRef.current = now;
    if (dt > 0) velocityRef.current = dx / dt * 16; // px per frame @60fps
    advanceByDelta(dx);
  }, [advanceByDelta]);

  const onMouseUp = useCallback(() => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    setIsDragging(false);
    momentumRef.current = requestAnimationFrame(applyMomentum);
  }, [applyMomentum]);

  // ── Touch handlers ─────────────────────────────────────────────────────────

  const onTouchStart = useCallback((e) => {
    e.preventDefault();
    cancelMomentum();
    if (autoRotateRef.current) { clearInterval(autoRotateRef.current); setIsAutoRotating(false); }
    isDraggingRef.current = true;
    setIsDragging(true);
    setDragHint(false);
    startXRef.current    = e.touches[0].clientX;
    lastXRef.current     = e.touches[0].clientX;
    velocityRef.current  = 0;
    lastTimeRef.current  = performance.now();
    accumulatedRef.current = 0;
  }, []);

  const onTouchMove = useCallback((e) => {
    if (!isDraggingRef.current || !e.touches) return;
    const now  = performance.now();
    const dt   = now - lastTimeRef.current;
    const dx   = e.touches[0].clientX - lastXRef.current;
    lastXRef.current    = e.touches[0].clientX;
    lastTimeRef.current = now;
    if (dt > 0) velocityRef.current = dx / dt * 16;
    advanceByDelta(dx);
  }, [advanceByDelta]);

  const onTouchEnd = useCallback(() => {
    isDraggingRef.current = false;
    setIsDragging(false);
    momentumRef.current = requestAnimationFrame(applyMomentum);
  }, [applyMomentum]);

  // ── Global mouse listeners (so drag works outside the element) ─────────────
  useEffect(() => {
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup",   onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup",   onMouseUp);
    };
  }, [onMouseMove, onMouseUp]);

  // ── Controls ───────────────────────────────────────────────────────────────

  const prev = () => images?.length && setCurrentIndex(i => (i - 1 + images.length) % images.length);
  const next = () => images?.length && setCurrentIndex(i => (i + 1) % images.length);

  const toggleAutoRotate = () => {
    if (!images?.length) return;
    if (isAutoRotating) {
      clearInterval(autoRotateRef.current);
      setIsAutoRotating(false);
    } else {
      setIsAutoRotating(true);
      autoRotateRef.current = setInterval(() => {
        currentIdxRef.current = (currentIdxRef.current + 1) % images.length;
        setCurrentIndex(currentIdxRef.current);
      }, 80);  // ~12 fps auto-rotate — smooth
    }
  };

  // ── Route to sub-viewers ───────────────────────────────────────────────────

  if (type === "hotspots") {
    return <CarHotspotViewer car={car} />;
  }

  if (type === "interior") {
    if (videoUrl) {
      const fullUrl = videoUrl.startsWith("http") ? videoUrl : `http://localhost:3001${videoUrl}`;
      return (
        <InteriorVideoPlayer
          videoUrl={fullUrl}
          poster={images?.[0] ? `http://localhost:3001${images[0]}` : null}
        />
      );
    }
    if (!images?.length) {
      return <div className="viewer-360-empty"><div className="empty-icon">🪑</div><p>Interior images not available</p></div>;
    }
    // fall through → spin viewer with interior images
  }

  if (type === "gallery") {
    if (!images?.length) {
      return <div className="viewer-360-empty"><div className="empty-icon">📷</div><p>No gallery images available</p></div>;
    }
    return (
      <div className="gallery-grid-wrapper">
        <div className="gallery-grid">
          {images.map((img, idx) => (
            <div key={idx} className="gallery-item">
              <img src={`http://localhost:3001${img}`} alt={`Gallery ${idx + 1}`} loading="lazy" />
              <span className="gallery-number">{idx + 1}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!images?.length) {
    return <div className="viewer-360-empty"><div className="empty-icon">📸</div><p>No {type} images available</p></div>;
  }

  // ── 360° Spin Viewer ───────────────────────────────────────────────────────
  return (
    <div className="car-360-viewer">

      {/* Badge */}
      <div className="viewer-360-badge">
        <svg className="spin-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        <span>360° Spin View</span>
      </div>

      {/* Drag hint — fades out after 3 s */}
      <div className={`viewer-360-instruction ${dragHint ? "hint-visible" : "hint-hidden"}`}>
        <p>💡 Drag left / right to rotate</p>
      </div>

      {/* Main image area */}
      <div
        ref={containerRef}
        className={`viewer-360-image-container ${isDragging ? "dragging" : ""}`}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <img
          src={`http://localhost:3001${images[currentIndex]}`}
          alt={`${type} view ${currentIndex + 1}`}
          className="viewer-360-image"
          draggable="false"
        />
        {/* Subtle spin cursor overlay */}
        {!isDragging && (
          <div className="spin-cursor-hint">
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
              <path d="M8 20 C8 13.4 13.4 8 20 8" stroke="#003566" strokeWidth="2.5" strokeLinecap="round"/>
              <path d="M32 20 C32 26.6 26.6 32 20 32" stroke="#003566" strokeWidth="2.5" strokeLinecap="round"/>
              <path d="M5 17 L8 20 L11 17" stroke="#003566" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M35 23 L32 20 L29 23" stroke="#003566" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        )}
      </div>

      {/* Progress bar */}
      <div className="viewer-360-progress">
        <span className="progress-text">{currentIndex + 1} / {images.length}</span>
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${((currentIndex + 1) / images.length) * 100}%` }} />
        </div>
      </div>

      {/* Arrow buttons */}
      <button className="viewer-360-arrow arrow-left" onClick={prev} title="Previous">
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <button className="viewer-360-arrow arrow-right" onClick={next} title="Next">
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Auto-rotate toggle */}
      <button
        className={`auto-rotate-btn ${isAutoRotating ? "rotating" : ""}`}
        onClick={toggleAutoRotate}
        title={isAutoRotating ? "Pause rotation" : "Auto rotate"}
      >
        {isAutoRotating
          ? <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>
          : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
        }
      </button>
    </div>
  );
};

export default Car360Viewer;